import { isEmpty } from "@bodil/core/assert";
import { attribute, attributeGetter, Component, type Emits } from "@bodil/dom/component";
import { visibilityObserver } from "@bodil/dom/dom";
import type { Role } from "aria-attribute-types";
import { html } from "lit";
import { customElement } from "lit/decorators.js";
import { when } from "lit/directives/when.js";

import { ListNavigationController } from "./controllers/list-navigation";
import { KeyboardCommand, keymapLookup } from "./keymap";

import styles from "./menu.css?inline";

declare global {
    interface HTMLElementTagNameMap {
        "btk-menu": BtkMenu;
    }
}

@customElement("btk-menu")
export class BtkMenu extends Component {
    emits!: Emits<"btk-popup-hide">;

    @attribute accessor headerTitle: string | null = null;
    @attribute accessor headerIcon: string | null = null;

    @attributeGetter get role(): Role {
        return "menu";
    }

    get items(): Array<HTMLElement> {
        return this.querySlot({
            flatten: true,
            selector: `[role^="menuitem"]`,
            instanceOf: HTMLElement,
        });
    }

    lastActiveItem?: HTMLElement;

    listNavigationController = new ListNavigationController<HTMLElement>(this, {
        root: () => this,
        predicate: (el): el is HTMLElement =>
            el instanceof HTMLElement && el.getAttribute("role")?.startsWith("menuitem") === true,
        activate: this.activateItem.bind(this),
    });

    override focus(): void {
        const currentItem = this.getCurrentItem();
        if (currentItem === undefined) {
            super.focus();
        } else {
            currentItem.focus();
        }
    }

    activateItem(currentItem: HTMLElement | undefined) {
        if (this.lastActiveItem !== undefined) {
            this.lastActiveItem.setAttribute("tabindex", "-1");
            this.lastActiveItem.removeAttribute("aria-current");
        }
        this.lastActiveItem = currentItem;
        if (currentItem !== undefined) {
            currentItem.setAttribute("tabindex", "0");
            currentItem.setAttribute("aria-current", "true");
            this.setAttribute("tabindex", "-1");
            currentItem.focus();
        } else {
            this.setAttribute("tabindex", "0");
        }
    }

    getCurrentItem(): HTMLElement | undefined {
        return this.listNavigationController.getActive();
    }

    setCurrentItem(currentItem: HTMLElement | undefined) {
        this.listNavigationController.setActive(currentItem);
    }

    setCurrentIndex(index: number) {
        this.setCurrentItem(this.items[index]);
    }

    activateFirstItem() {
        this.setCurrentItem(this.items[0]);
    }

    protected override connected() {
        this.useWhileConnected(
            this.on("keydown", (e) => this.handleKeyDown(e)),
            this.on("click", (e) => e.stopPropagation()),
            visibilityObserver(this, (oldState, newState) => {
                if (oldState && !newState) {
                    this.setCurrentItem(undefined);
                }
            }),
        );
    }

    protected override firstUpdated(): void {
        super.firstUpdated();
        this.setCurrentItem(undefined);
    }

    private handleKeyDown(e: KeyboardEvent) {
        const cmd = keymapLookup(e);
        switch (cmd) {
            case KeyboardCommand.Dismiss:
                this.emit("btk-popup-hide");
                break;

            case KeyboardCommand.Enter:
                this.getCurrentItem()?.click();
                break;

            default:
                return;
        }
        e.preventDefault();
        e.stopPropagation();
    }

    private handleMouseDown(e: MouseEvent) {
        if (
            e.target instanceof HTMLElement &&
            e.target.getAttribute("role")?.startsWith("menuitem") === true
        ) {
            this.activateItem(e.target);
        }
    }

    private handleSlotChange() {
        this.activateItem(undefined);
    }

    protected override render(): unknown {
        return html`${when(
                !isEmpty(this.headerTitle),
                () => html`
                    <div class="header" role="heading" title=${this.headerTitle!}>
                        ${when(
                            !isEmpty(this.headerIcon),
                            () => html`<img src=${this.headerIcon!} />`,
                        )}
                        ${this.headerTitle}
                    </div>
                `,
            )}
            <slot @slotchange=${this.handleSlotChange} @mousedown=${this.handleMouseDown}></slot>`;
    }

    static styles = styles;
}
